import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Paper,
    TextField,
    Typography,
    useTheme,
    alpha,
    IconButton,
    InputAdornment,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import ErrorAlert from '../common/ErrorAlert';

const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    // Mensagem deixada pelo interceptor quando a sessão expira. Só é removida no efeito:
    // no StrictMode o inicializador roda duas vezes e a mensagem se perderia.
    const [error, setError] = useState<string | null>(() => sessionStorage.getItem('@App:authError'));
    useEffect(() => {
        sessionStorage.removeItem('@App:authError');
    }, []);
    const navigate = useNavigate();
    const { login } = useAuth();
    const { t } = useTranslation();
    const theme = useTheme();

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null); // Limpa erros anteriores
        
        if (!email || !password) {
            setError(t('errors.fillAllFields'));
            return;
        }

        try {
            await login(email, password);
            const storedUser = localStorage.getItem('@App:user');
            const user = storedUser ? JSON.parse(storedUser) : null;
            
            if (!user) {
                setError(t('errors.userNotFound'));
                return;
            }
            
            // Usa replace: true para evitar voltar para a página de login
            if (user.must_change_password) {
                // Conta criada/redefinida pelo RH (ou admin inicial): troca de senha obrigatória
                navigate('/profile', { replace: true });
            } else if (user.role === 'Gestor' || user.role === 'Diretor') {
                navigate('/gestor/dashboard', { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        } catch (err: any) {
            const errorMessage = 
                err.response?.data?.message || // Mensagem específica da API
                err.response?.data?.error || // Erro específico da API
                err.message || // Mensagem de erro do JavaScript
                t('errors.login'); // Mensagem padrão de erro
            
            setError(errorMessage);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                background: `radial-gradient(circle at 50% 50%, ${alpha(theme.palette.primary.dark, 0.15)} 0%, ${theme.palette.background.default} 70%)`,
                position: 'relative',
                overflow: 'hidden',
                '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px white inset',
                    WebkitTextFillColor: theme.palette.text.primary,
                },
                '& input:-webkit-autofill:focus': {
                    WebkitBoxShadow: '0 0 0 1000px white inset',
                    WebkitTextFillColor: theme.palette.text.primary,
                },
            }}
        >
            {/* Círculos decorativos */}
            <Box
                sx={{
                    position: 'absolute',
                    width: '50vw',
                    height: '50vw',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0)} 70%)`,
                    top: '-25vw',
                    left: '-25vw',
                    zIndex: 0,
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    width: '40vw',
                    height: '40vw',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0)} 70%)`,
                    bottom: '-20vw',
                    right: '-20vw',
                    zIndex: 0,
                }}
            />

            <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src="/logo.svg"
                        alt="Logo"
                        sx={{
                            width: 180,
                            height: 'auto',
                            mb: 4,
                            filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1))',
                        }}
                    />

                    <Paper
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            p: 4,
                            width: '100%',
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        }}
                        elevation={0}
                    >
                        <Typography 
                            component="h1" 
                            variant="h5" 
                            align="center" 
                            gutterBottom
                            sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 600,
                                mb: 3,
                            }}
                        >
                            {t('auth.login.title')}
                        </Typography>

                        {error && (
                            <ErrorAlert
                                error={error}
                                onClose={() => setError(null)}
                            />
                        )}

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label={t('auth.login.email')}
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                },
                            }}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label={t('auth.login.password')}
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            size="small"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                            size="small"
                                            sx={{ color: theme.palette.text.secondary }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                },
                            }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2,
                                backgroundColor: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                },
                            }}
                        >
                            {t('auth.login.submit')}
                        </Button>

                        {/* Cadastro público desativado: os acessos são criados pelo RH/Diretor */}
                        <Typography variant="body2" align="center" color="text.secondary">
                            {t('auth.login.noAccess')}
                        </Typography>
                    </Paper>
                </Box>
            </Container>
        </Box>
    );
};

export default Login; 